import { init, type ErrorEvent as SentryErrorEvent } from '@sentry/browser';
import { getMicrofrontendClusterName, getMicrofrontendEnvironmentName } from '@src/utils/environment.js';

export interface SetupBugsinkDsnsI {
  [key: string]: string | undefined;
  dev: string | undefined;
  prod: string | undefined;
}

export interface SetupBugsinkI {
  /**
   * DSNS for all accounts
   * @param {SetupBugsinkDsnsI} [dsns] - DNS for keys "dev" and "prod"
   */
  dsns: SetupBugsinkDsnsI;
  /** Version of the app from package.json */
  version?: string;
  /** Force enable bugsink integration pointing at bugsink on "dev" or "prod" */
  forceCluster?: string;
}

/**
  Only send errors to sentry, if they occured inside a micro-frontend (tef-coms*.js)

  "event" has the following structure:
  {
  "exception": {
    "values": [
      {
        "type": "Error",
        "value": "Demo Error!",
        "stacktrace": {
          "frames": [
            {
              "filename": "http://st2.wetteronline.de:9090/integration.html#",
              "function": "HTMLAnchorElement.onclick",
              "in_app": true,
              "lineno": 13,
              "colno": 106
            }, ...
 */
export const beforeSend = (event: SentryErrorEvent) => {
  const frames = event?.exception?.values?.[0].stacktrace?.frames;

  // There will only be one occurance of "tef-coms" for errors thrown by other webseits. Those we don't want!
  // The sentry global window.onerror handler will be in a "tef-coms" file so we need to have more than that to trigger a sending.
  if (Array.isArray(frames)) {
    const occurances = frames.filter((frame) => frame.filename?.includes('tef-coms'));
    if (occurances.length > 1) {
      return event;
    }
  }

  // Suppress event sending
  return null;
};

// https://docs.sentry.io/platforms/javascript/configuration/options/#ignoreErrors
// Strings are partially matched, regex are fully matched
const IGNORE_ERRORS_WITH_MESSAGES = [
  'Cannot load customer with customer id', // HTTP 400 : "{\"message\":\"Cannot load customer with customer id \\\"C...
  'Failed to fetch (api.eu.usercentrics.eu)', // If using ad blocker
  'HTTP 409', // HTTP 409 : "{\"statusCode\":409,\"error\":\"Conflict\"
  'Invalid Credentials', // HTTP 401 : "{\"code\":\"900901\",\"message\":\"Invalid Credentials\",\"description\":\"Invalid Credentials. Make sure you have provided the correct security credentials\"}"
  'Missing authentication', // tef-coms: HTTP 401 : "{\"statusCode\":401,\"error\":\"Unauthorized\",\"message\":\"Missing authentication\"}"
  'Unauthorized', // HTTP 401 : "{\"statusCode\":401,\"error\":\"Unauthorized\",\"message\":\"error details hidden\"}"
  'invalid contact channel', // tef-coms: See api-consent/src/plugins/modelValidation.ts
  'invalid lifecycle', // tef-coms: See api-consent/src/plugins/modelValidation.ts
  'invalid pack', // tef-coms: See api-consent/src/plugins/modelValidation.ts
  'invalid service provider id', // tef-coms: See api-consent/src/plugins/modelValidation.ts
  /^.*"tef-coms-references".*\.source" is required.*$/, // tef-coms: Intential error message
];

export const setupBugsink = (options: SetupBugsinkI): void => {
  const environment = getMicrofrontendEnvironmentName();
  const cluster = options.forceCluster ?? getMicrofrontendClusterName(environment);

  if (!cluster || !options.dsns[cluster]) {
    // Silently fail on undefined dsn or development
    return;
  }

  // See https://docs.sentry.io/platforms/javascript/configuration/
  init({
    // https://docs.sentry.io/platforms/javascript/configuration/options/#denyUrls
    denyUrls: [
      'test-app.js',
    ],
    beforeSend,
    dsn: options.dsns[cluster],
    enabled: true,
    environment,
    ignoreErrors: IGNORE_ERRORS_WITH_MESSAGES,
    integrations: (defaults) => defaults.filter((i) => i.name !== 'BrowserSession'), // removes call with type=session
    profileSessionSampleRate: 0, // https://docs.sentry.io/platforms/javascript/configuration/options/#profiling-options
    release: options.version || 'unknown',
    replaysSessionSampleRate: 0, // https://docs.sentry.io/platforms/javascript/configuration/options/#replaysSessionSampleRate
    sendClientReports: false, // Not supported by bugsink
    sendDefaultPii: true,
    tracesSampleRate: 0,
  });
};
